import { useState } from "react";
import axios from "axios";
import { API, setToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Lock } from "lucide-react";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password });
      setToken(data.token);
      onLogin?.();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-40" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #064e3b 0, transparent 40%), radial-gradient(circle at 80% 70%, #0f172a 0, transparent 45%)" }} />
      <div className="relative w-full max-w-sm">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="h-11 w-11 rounded-xl bg-slate-900 border border-white/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-emerald-400" />
          </div>
          <div>
            <div className="font-head font-extrabold tracking-tight text-xl text-white leading-none">BidDesk</div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-emerald-400 mt-1">GeM Docs</div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-7 shadow-2xl" data-testid="login-form">
          <div className="flex items-center gap-2 text-white mb-1">
            <Lock className="h-4 w-4 text-emerald-400" />
            <h1 className="font-head font-bold text-lg">Admin Login</h1>
          </div>
          <p className="text-xs text-slate-400 mb-6">Sign in to access your bidding workspace.</p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold text-slate-300">User ID</Label>
              <Input data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus placeholder="admin" className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-300">Password</Label>
              <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5 bg-white/5 border-white/10 text-white placeholder:text-slate-500" />
            </div>
          </div>

          {error && <div className="mt-4 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}

          <Button type="submit" disabled={loading || !email || !password} className="w-full mt-6 rounded-lg bg-emerald-600 hover:bg-emerald-500 border-0" data-testid="login-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign In"}
          </Button>
        </form>
        <p className="text-center text-[11px] text-slate-500 mt-6">Protected workspace · single-admin access</p>
      </div>
    </div>
  );
}
